import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Heading from 'components/shared/heading';
import Card from './components/Card';
import styles from './styles.module.scss';

/**
 * Gatsby image markup uses inline styles that differ between SSR and client (e.g. opacity fade-in).
 * Recursively mark the subtree so React skips attribute diffing and avoids hydration warnings.
 */
function withSuppressHydrationWarningTree(node) {
  if (!React.isValidElement(node)) {
    return node;
  }

  const processedChildren = React.Children.map(node.props.children, (child) =>
    withSuppressHydrationWarningTree(child)
  );

  return React.cloneElement(node, { suppressHydrationWarning: true }, processedChildren);
}

function parseImageHeight(value) {
  if (value === undefined || value === null || value === '') return undefined;
  const n = typeof value === 'number' ? value : Number.parseInt(String(value), 10);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

const FactSheetsGrid = ({
  title,
  description = undefined,
  items = [],
  imageHeight: imageHeightProp = undefined,
  children = null,
}) => {
  const childrenArray = useMemo(
    () => React.Children.toArray(children).filter((child) => React.isValidElement(child)),
    [children]
  );

  const imageHeight = useMemo(() => parseImageHeight(imageHeightProp), [imageHeightProp]);

  return (
    <section className={styles.section}>
      {(title || description) && (
        <div className={styles.header}>
          {title && <Heading className={styles.title} tag="h2" size="xl" innerHTML={title} />}
          {description && (
            <div className={styles.description} dangerouslySetInnerHTML={{ __html: description }} />
          )}
        </div>
      )}
      <div className={styles.inner}>
        <div className="container">
          <div className={styles.grid}>
            {items?.map(({ title: itemTitle, link }, index) => (
              <Card
                key={index}
                link={link}
                title={itemTitle}
                imageHeight={imageHeight}
                imageNode={
                  childrenArray[index]
                    ? withSuppressHydrationWarningTree(childrenArray[index])
                    : null
                }
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

FactSheetsGrid.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      link: PropTypes.string.isRequired,
    })
  ),
  imageHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  children: PropTypes.node,
};

export default FactSheetsGrid;
